/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

export interface FormState {
    role: string;
    UUID: string;
    starterInfo: StarterInfo;
    contractor: Contractor;
    // client: Client;
    status: string;
    signatureId: string;
}

export interface ClientFormState {
    role: string;
    UUID: string;
    starterInfo: StarterInfo;
    client: Client;
    status: string;
}

export type RoleTypes = 'contractor' | 'client';

export interface StarterInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    smsOptIn: boolean;
}

export interface AccountsPayableRepresentative {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface Contractor {
    totalSteps: number;
    companyInfo: CompanyInfo;
    teamInfo: TeamInfo;
    serviceArea: string;
    documents: ContractorDocuments;
    trades: Trade[];
    registerForLaborJobs: boolean;
    partsEquipmentOrdering: PartsEquipmentOrdering;
}

export interface PartsEquipmentOrdering {
    forMyCompany: boolean;
    onBehalfOfClient: boolean;
    neither: boolean;
    clients: DropdownOption[];
}

export interface Trade {
    type: string;
    subCategories: string[];
}
interface Client {
    totalSteps: number;
    companyInfo: CompanyInfo;
    accountsPayableRepresentative: AccountsPayableRepresentative;
    documents: ClientDocuments;
}
export interface Address {
    address1: string;
    address2: string;
    city: string;
    zip: string;
    state: string;
}
export interface CompanyInfo {
    name: string;
    website: string;
    corporationType: string;
    doingBusinessAs?: string;
    companyAddress: Address;
    billingAddress: Address;
    useCompanyAddressAsBilling: boolean;
    headquarteredInCalifornia?: boolean;
    useNameAsDBA?: boolean;
}
export interface TeamInfo {
    systemInstalls: string;
    serviceCalls: string;
    masterTechs: string;
    employees: string;
    singleFamilyServiceProvider: boolean;
    multiDwellingServiceProvider: boolean;
    commercialServiceProvider: boolean;
}

export interface ContractorDocuments {
    epaCertification: DocFile[];
    secretaryOfStateRegistration: DocFile[];
    w9: DocFile[];
    tradeLicenses: Qualification[];
    certificateOfLiability: DocFile[];
    taxSalesCertificate: DocFile[];
}

export interface ClientDocuments {
    w9: DocFile[];
}

export interface Qualification {
    type: string;
    fileName: string;
    tradeTypeId: string;
    tradeSubTypeId: string;
    file: DocFile;
}

export interface DocFile {
    category: string;
    name: string;
    resourceType: string;
    key: string | null;
    url: string;
    secureUrl: string;
    timeStamp: string;
    validFrom: string | null;
    validTo: string | null;
    taxIdNumber?: string | null;
    taxIdType?: string | null;
    state?: string | null;
    bucket: string;
    source: string;
}

export enum ParagraphListTypes {
    ORDERED = 'ordered',
    UNORDERED = 'unordered',
}

export enum TradeTypeId {
    HVAC = 'HVAC',
    ELECTRICAL = 'ELECTRICAL',
    PLUMBING = 'PLUMBING',
}

export enum TradeSubTypeId {
    RESIDENTIAL = 'RESIDENTIAL',
    COMMERCIAL = 'COMMERCIAL',
    WATER_HEATER = 'WATER_HEATER',
}

export enum CONTRACTOR_QUALIFICATION_TYPE {
    BACKGROUND_CHECK = 'BACKGROUND_CHECK',
    CERTIFICATION = 'CERTIFICATION',
    LICENSE = 'LICENSE',
}

export enum DOCUMENT_TYPES {
    certificateOfLiability = 'certificateOfLiability',
    epaCertification = 'epaCertification',
    secretaryOfStateRegistration = 'secretaryOfStateRegistration',
    w9 = 'w9',
    tradeLicenses = 'tradeLicenses',
    taxSalesCertificate = 'taxSalesCertificate',
}

export enum DOCUMENT_CATEGORIES {
    W9 = 'W9',
    EPA = 'EPA',
    PROOF_OF_INSURANCE = 'PROOF_OF_INSURANCE',
    QUALIFICATIONS = 'QUALIFICATIONS',
    FLAT_RATE_PAYOUT = 'FLAT_RATE_PAYOUT',
    COMPANY_MSA = 'COMPANY_MSA',
    DATA_CONSENT_AGREEMENT = 'DATA_CONSENT_AGREEMENT',
    TAX_SALES_CERTIFICATE = 'TAX_SALES_CERTIFICATE',
    SECRETARY_OF_STATE_REGISTRATION = 'SECRETARY_OF_STATE_REGISTRATION',
}

export const DOCUMENT_CATEGORY_OPTIONS: Record<
    DOCUMENT_CATEGORIES,
    { id: DOCUMENT_CATEGORIES; display: string }
> = {
    [DOCUMENT_CATEGORIES.W9]: {
        id: DOCUMENT_CATEGORIES.W9,
        display: 'W9',
    },
    [DOCUMENT_CATEGORIES.EPA]: {
        id: DOCUMENT_CATEGORIES.EPA,
        display: 'EPA Certifications',
    },
    [DOCUMENT_CATEGORIES.PROOF_OF_INSURANCE]: {
        id: DOCUMENT_CATEGORIES.PROOF_OF_INSURANCE,
        display: 'Proof of Insurance',
    },
    [DOCUMENT_CATEGORIES.QUALIFICATIONS]: {
        id: DOCUMENT_CATEGORIES.QUALIFICATIONS,
        display: 'Qualifications',
    },
    [DOCUMENT_CATEGORIES.FLAT_RATE_PAYOUT]: {
        id: DOCUMENT_CATEGORIES.FLAT_RATE_PAYOUT,
        display: 'Flat Rate Payout Agreement',
    },
    [DOCUMENT_CATEGORIES.COMPANY_MSA]: {
        id: DOCUMENT_CATEGORIES.COMPANY_MSA,
        display: 'Company MSA',
    },
    [DOCUMENT_CATEGORIES.DATA_CONSENT_AGREEMENT]: {
        id: DOCUMENT_CATEGORIES.DATA_CONSENT_AGREEMENT,
        display: 'Data Consent Agreement',
    },
    [DOCUMENT_CATEGORIES.TAX_SALES_CERTIFICATE]: {
        id: DOCUMENT_CATEGORIES.TAX_SALES_CERTIFICATE,
        display: 'Tax Sales Certificate',
    },
    [DOCUMENT_CATEGORIES.SECRETARY_OF_STATE_REGISTRATION]: {
        id: DOCUMENT_CATEGORIES.SECRETARY_OF_STATE_REGISTRATION,
        display: 'Secretary of State Registration Certification',
    },
};

export enum RequiredFormFields {
    effectiveDate = 'effectiveDate',
    expirationDate = 'expirationDate',
    issueDate = 'issueDate',
    taxIdNumber = 'taxIdNumber',
    taxIdType = 'taxIdType',
    state = 'state',
    tradeType = 'tradeType',
}

export type DocumentLink = {
    label: string;
    url: string;
};

export type DOCUMENT_UPLOAD_FIELDS = {
    [DOCUMENT_TYPES.certificateOfLiability]: {
        label: string;
        paragraphContent: {
            intro: string;
            listType: ParagraphListTypes;
            listItems: string[];
            outro: string;
        };
        requiredFormFields: RequiredFormFields[];
    };
    [DOCUMENT_TYPES.epaCertification]: {
        label: string;
        paragraphText: string;
        requiredFormFields: RequiredFormFields[];
    };
    [DOCUMENT_TYPES.secretaryOfStateRegistration]: {
        label: string;
        paragraphContent: {
            intro: string;
            listType: ParagraphListTypes;
            listItems: string[];
            outro: string;
        };
        links: DocumentLink[];
        requiredFormFields: RequiredFormFields[];
    };
    [DOCUMENT_TYPES.w9]: {
        label: string;
        paragraphContent: {
            intro: string;
            listType: ParagraphListTypes;
            listItems: string[];
            outro: string;
        };
        requiredFormFields: RequiredFormFields[];
    };
    [DOCUMENT_TYPES.tradeLicenses]: {
        label: string;
        paragraphText: string;
        requiredFormFields: RequiredFormFields[];
    };
    [DOCUMENT_TYPES.taxSalesCertificate]: {
        label: string;
        paragraphText: string;
        requiredFormFields: RequiredFormFields[];
    };
};

export type DropdownOption = {
    value: string;
    label: string;
};
